import { z } from 'zod';
import { ERROR_CODES } from '../enums';

/**
 * Building blocks every resource DTO reuses (API_DOCS.md — Conventions).
 */

/**
 * A decimal number transported as a string. All money/quantity/price values on the wire
 * use this (AI_RULES §5) — the server converts with `new Decimal(...)`, never `Number()`.
 */
export const decimalStringSchema = z
  .string()
  .regex(/^-?(?:\d+)(?:\.\d+)?$/, 'must be a decimal string, e.g. "142.31"');

export const isoTimestampSchema = z.string().datetime({ offset: false });

/**
 * Compares two decimal strings exactly, with zero floating point involved: both sides are
 * padded to a common scale and compared as BigInts (AI_RULES §5 — no `Number()` on money).
 * Returns -1, 0 or 1. Inputs must already match `decimalStringSchema`.
 */
export function compareDecimalStrings(a: string, b: string): -1 | 0 | 1 {
  const parse = (v: string): { neg: boolean; int: string; frac: string } => {
    const neg = v.startsWith('-');
    const body = neg ? v.slice(1) : v;
    const dot = body.indexOf('.');
    return dot === -1
      ? { neg, int: body, frac: '' }
      : { neg, int: body.slice(0, dot), frac: body.slice(dot + 1) };
  };
  const pa = parse(a);
  const pb = parse(b);
  const scale = Math.max(pa.frac.length, pb.frac.length);
  const toBig = (p: { neg: boolean; int: string; frac: string }): bigint => {
    const digits = `${p.int}${p.frac.padEnd(scale, '0')}`;
    const magnitude = BigInt(digits);
    return p.neg ? -magnitude : magnitude;
  };
  const na = toBig(pa);
  const nb = toBig(pb);
  return na < nb ? -1 : na > nb ? 1 : 0;
}

const DECIMAL_STRING = /^-?(?:\d+)(?:\.\d+)?$/;

/**
 * Refines a decimal string to an inclusive [min, max] range without float math.
 * zod runs refinements even after the regex check failed, so a non-decimal input must be
 * answered with `false` here — handing it to BigInt would throw out of `parse()`.
 */
export function decimalInRange(min: string, max: string) {
  return (v: string): boolean =>
    DECIMAL_STRING.test(v) && compareDecimalStrings(v, min) >= 0 && compareDecimalStrings(v, max) <= 0;
}

export const uuidSchema = z.string().uuid();

/** jsonb columns and free-form payloads are `unknown` until a narrower schema parses them. */
export const jsonValueSchema: z.ZodType<JsonValueDto> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);
export type JsonValueDto =
  | string
  | number
  | boolean
  | null
  | JsonValueDto[]
  | { [key: string]: JsonValueDto };

export const jsonObjectSchema = z.record(jsonValueSchema);

/** Error envelope returned by every non-2xx response. */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    /** Field-level detail for VALIDATION_ERROR; omitted otherwise. */
    details: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** `{ items, page, pageSize, total }` for any item schema. */
export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  });
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export const emptyResponseSchema = z.void();
